// src/components/block/liveStreamManagement/LiveStreamLayoutV2.tsx
//
// V2.5 — Ajout des 4 props intercomm :
//         intercommMicStream, intercommDeviceStreams,
//         intercommSpeakingDevices, onIntercommActivate
//         → passées en pass-through à MultiviewPanelV2

import React, { useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Power } from 'lucide-react';

import {
  VideoSource, AudioOnlyVisual, RemoteConnection,
  ImageSlot, SoundSlot, VideoOverlaySlot, JingleSlot, LayoutSlot,
  ScreenShareSlot, SourceAudioConfig,
} from '../../../contexts/MediaStreamContext';
import { RemoteDevice, DeviceAuthMode } from '../../../dataTypes/liveStreamManagment/types_webrtc';
import { DeviceGeoReport } from '../../../dataTypes/liveStreamManagment/types_media';
import { RegisteredRemoteDevice } from '../../../backendServices/liveStreamManagment/api_remoteDevices';
import { DeckKeyMap } from '../../../dataTypes/liveStreamManagment/types_deck';
import { XtreamCredentials } from '../../../dataTypes/liveStreamManagment/sourceRegistry/types';
import { MultiviewPanelV2, MultiviewSlotConfig } from './MultiviewPanelV2';
import { PortalTargetContext, useDetachedWindow, KleonLogo } from './DetachedWindow';
import DeckV2                                    from './DeckV2';
import ControlBar                                from './ControlBar';
import { MediaboardConfigModal, ConfigTab }       from './ProductionControlRoom_MediaboardConfig';
import { KeyboardShortcutsModal }                from './ProductionControlRoom_KeyboardShortcuts';
import {
  SourceAliasConfigModal, SourceAliasMap,
} from './SourceAliasConfigModal';
import { AudioEffectState, AudioEffectId, StereoVolume } from './SourceConfigModal';
import { VideoEffectsSettings } from './videoEffects/VideoEffectsPipelineManager';
import { HlsProbeStatus } from './useHlsProber';

import { WWW_URLS } from '../../../backendServices/urls';

import { AudioSwitchState } from './ControlBar';
import { AutoDirectorSettings } from './autoDirector';
import { AutoDirectorStatus } from './useAutoDirector';
import { AutoDirectorCandidate } from './AutoDirectorSettingsModal';

import { BroadcastingChannel, StreamSource } from '../../../dataTypes/broadcastingManagment/types';

import { AudioSourceRegistry } from '../../../dataTypes/liveStreamManagment/audioPipeline/AudioSourceRegistry';

// ─── Props ────────────────────────────────────────────────────────────────────

interface LiveStreamLayoutV2Props {
  /**
   * Cette régie pilote-t-elle ? En observation, tout le pupitre est rendu inerte
   * par le parent (`pointer-events`), et le deck relève cette inertie pour les
   * SEULS boutons de prévisualisation : regarder un flux HLS, Xtream ou RTMP est
   * une affaire locale au navigateur, elle n'agit ni sur l'antenne ni sur les
   * téléphones.
   */
  isHolder?: boolean;
  previewMuted          : boolean;
  onTogglePreviewMuted  : () => void;
  finalMuted         : boolean;
  onToggleFinalMuted : () => void;
  canvasPreviewRef  : React.RefObject<HTMLVideoElement>;
  hlsMonitorStream?: MediaStream | null;
  hasCurrentStream  : boolean;
  isStreaming       : boolean;
  isStreamButtonActive : boolean;
  activeJingleId    : string | null;
  isStartingStream : boolean;
  isStoppingStream : boolean;
  onStartStreaming : () => void;
  onStopStreaming  : () => void;
  isRecording          : boolean;
  isTogglingRecording  : boolean;
  recordingElapsedSec  : number;
  remainingRecordingSec?: number | null;
  onStartRecording     : () => void;
  onStopRecording      : () => void;
  onOpenStorage : () => void;
  remoteDevices        : RemoteDevice[];
  remoteConnections    : Map<string, RemoteConnection>;
  serverUrl            : string;
  streamPath           : string | null;   // null = aucune chaîne identifiée, aucun repli
  onLoadDevices        : () => void;
  onRegisterDevice     : (id: string, label: string, password: string, authMode?: DeviceAuthMode, options?: { silent?: boolean; accessToken?: string }) => Promise<RegisteredRemoteDevice | undefined>;
  onDeleteDevice       : (device_id: string) => void;
  onDisconnectDevice   : (device_id: string) => void;
  onRegeneratePassword : (device_id: string) => Promise<void>;
  onSetDirector        : (device_id: string, active: boolean) => void;
  directorDeviceIds    : Set<string>;
  deviceNetworkQuality : Map<string, { level: number; sample: any }>;
  /** Position et visée des appareils, pour l'onglet Carte du multiview. */
  deviceGeo?           : Map<string, DeviceGeoReport>;
  /** Abonne les appareils indiqués à l'émission de leur position (onglet Carte). */
  onGeoWatch?          : (active: boolean, deviceIds: string[]) => void;
  onMetricsLog?        : (active: boolean, deviceIds: string[]) => void;
  /** La publication permanente du programme est-elle ouverte, et est-elle à l'antenne ? */
  programLinkActive?   : boolean;
  programLinkOnAir?    : boolean;
  onCloseProgramLink?  : () => void;
  onReopenProgramLink? : () => void;
  deckPreviewVideoRef  : React.RefObject<HTMLVideoElement>;
  deckPreviewLabel     : string;
  deckSlots            : (VideoSource | null)[];
  deckSlotLayouts      : (LayoutSlot | null)[];
  onSlotLayoutChange   : (slotIndex: number, layout: LayoutSlot | null) => void;
  onDeckPreviewLayout  : (layout: LayoutSlot) => void;
  activeLayoutPreviewId: string | null;
  selectedVideoSource  : string;
  onDeckSwitch         : (source: VideoSource, forceNewScreen?: boolean) => void;
  onDeckPreview        : (source: VideoSource) => void;
  onAcquirePreview     : (source: VideoSource) => Promise<void>;
  deckPreviewSourceId  : string | null;
  onDeckCut            : () => void;
  previewStreams        : Map<string, MediaStream>;
  // ── T-bar (fondu enchaîné preview → final) ───────────────────────────────
  tbarValue         : number;
  tbarValueRef      : React.MutableRefObject<number>;
  tbarBaseline      : 0 | 1;
  onTbarChange      : (v: number) => void;
  onTbarAuto        : () => void;
  tbarAutoActive    : boolean;
  tbarSpeedSec      : number;
  onTbarSpeedChange : (v: number) => void;
  tbarEnchainementId       : string;
  onTbarEnchainementChange : (id: string) => void;
  tbarEnchainementConfig       : unknown;
  onTbarEnchainementConfigChange : (config: unknown) => void;
  imageSlots           : ImageSlot[];
  activeImageIds       : Set<string>;
  onToggleImage        : (id: string) => void;
  soundSlots           : SoundSlot[];
  activeSoundIds       : Set<string>;
  onTriggerSound       : (slot: SoundSlot) => void;
  jingleSlots          : JingleSlot[];
  onTriggerJingle      : (slot: JingleSlot) => void;
  videoOverlaySlots    : VideoOverlaySlot[];
  activeVideoOverlayIds: Set<string>;
  pipVideoEls          : React.MutableRefObject<Map<string, { el: HTMLVideoElement; stream: MediaStream }>>;
  onStartVideoOverlay  : (slot: VideoOverlaySlot) => void;
  keyMap               : DeckKeyMap;
  onOpenKeyboardShortcuts : () => void;
  onOpenMediaboardConfig  : (tab?: ConfigTab, layoutIdx?: number) => void;
  showMediaboardConfig    : boolean;
  onCloseMediaboardConfig : () => void;
  onImageChange           : (slots: ImageSlot[]) => void;
  onSoundChange           : (slots: SoundSlot[]) => void;
  onPipChange             : (slots: VideoOverlaySlot[]) => void;
  onJingleChange          : (slots: JingleSlot[]) => void;
  loadedAudioBuffers      : Map<string, AudioBuffer>;
  showKeyboardShortcuts   : boolean;
  onCloseKeyboardShortcuts: () => void;
  onKeyMapChange          : (km: DeckKeyMap) => void;
  videoSlotLabels         : (string | null)[];
  imageSlotLabels         : string[];
  soundSlotLabels         : string[];
  pipSlotLabels           : string[];
  jingleSlotLabels        : string[];
  screenShareSlots     : ScreenShareSlot[];
  screenShareStreams   : Map<string, MediaStream>;
  onScreenSlotsChange  : (slots: ScreenShareSlot[]) => void;
  onScreenReset        : (slotId: string) => Promise<void>;
  channelActive: boolean;
  channelFullyActive: boolean;
  channelReadyForRecording: boolean;
  availableSources          : VideoSource[];
  slotConfigs               : MultiviewSlotConfig[];
  onSlotConfigsChange       : (configs: MultiviewSlotConfig[]) => void;
  onSlotsReorder            : (fromIndex: number, toIndex: number) => void;
  closeOnFullscreen         : boolean;
  onToggleCloseOnFullscreen : () => void;
  onOpenFullscreen          : (closePanel: boolean) => void;
  sourceAliases             : SourceAliasMap;
  onSourceAliasesChange     : (aliases: SourceAliasMap) => void;
  showSourceAliasConfig     : boolean;
  onOpenSourceAliasConfig   : () => void;
  onCloseSourceAliasConfig  : () => void;
  onOpenDeckFullscreen      : () => void;
  layoutSlots               : LayoutSlot[];
  activeLayoutId            : string | null;
  onToggleLayout            : (slot: LayoutSlot) => void;
  onLayoutChange            : (slots: LayoutSlot[]) => void;
  getSourceAudioConfig      : (sourceId: string) => SourceAudioConfig;
  onSourceVolumeChange      : (sourceId: string, volume: number) => void;
  onSourceMuteToggle        : (sourceId: string) => void;
  isDeckExternal            : boolean;
  isMultiviewExternal       : boolean;
  availableAudioDevices     : { deviceId: string; label: string; kind?: string }[];
  onSlotSourceChange        : (slotIndex: number, newSource: VideoSource | null) => void;
  onDeckSwitchSource        : (source: VideoSource) => void;
  onAliasChange             : (sourceId: string, alias: string) => void;
  selectedAudioDeviceIds    : Map<string, string>;
  onAudioDeviceChange       : (sourceId: string, audioDeviceId: string) => void;
  stereoVolumes             : Map<string, StereoVolume>;
  onStereoVolumeChange      : (sourceId: string, stereo: StereoVolume) => void;
  audioEffectsMap           : Map<string, AudioEffectState>;
  onToggleAudioEffect       : (sourceId: string, effect: AudioEffectId) => void;
  audioEffectConfigsMap?      : Map<string, Record<string, unknown>>;
  onAudioEffectConfigsChange? : (sourceId: string, configs: Record<string, unknown>) => void;
  videoEffectsSettingsMap?      : Map<string, VideoEffectsSettings>;
  onVideoEffectsSettingsChange? : (sourceId: string, settings: VideoEffectsSettings) => void;
  /** Image d'une source audio seul (écran noir / analyseur de spectre). */
  onAudioVisualChange?          : (sourceId: string, visual: AudioOnlyVisual) => void;
  onSlotSourceChangeAlias   : (slotIndex: number, newSource: VideoSource | null) => void;
  onSourceConfigOpen  : (sourceId: string, source: VideoSource | null) => Promise<void>;
  onSourceConfigClose : (sourceId: string | null) => void;
  hlsProbeStatusMap   : Map<string, HlsProbeStatus>;
  hlsWasEverOk        : (sourceId: string) => boolean;
  onRetryHls          : (sourceId: string, url: string) => void;
  onSlotPowerToggle   : (sourceId: string, powered: boolean) => void;
  poweredOffSlots     : Set<string>;
  streamSource?         : StreamSource;
  defaultStreamSource?  : StreamSource;
  onStreamSourceChange? : (source: StreamSource) => void;
  onStopStreamingExternal? : () => void;
  isRegiePowered      : boolean;
  onToggleRegiePower  : () => void;
  onOpenPupitreConfig?: () => void;
  onOpenDevicesPanel? : () => void;
  onSaveConfig : () => void;
  onLoadConfig : () => void;
  onResetRegie : () => void;
  onDefaultStreamSourceChange? : (source: StreamSource) => void;
  broadcastUuid? : string;
  onAddHlsSource?: (url: string, xtream?: XtreamCredentials, label?: string) => void;
  onRegisterXtreamConnection?: (url: string, xtream?: XtreamCredentials, label?: string, opts?: { autoRetry?: boolean }) => void;
  microserviceUrl?: string;
  audioSwitchState    ?: AudioSwitchState;
  onAudioSwitchChange ?: (state: AudioSwitchState) => void;
  deckAttachedAudioIds ?: string[];
  mixSourceIds ?: Record<string, string>;
  onMixerDraftChange ?: (draft: { gains: Record<string, number>; muted: Record<string, boolean>; monitorMuted?: boolean } | null) => void;
  audioSourceRegistry ?: import('../../../dataTypes/liveStreamManagment/audioPipeline/AudioSourceRegistry').AudioSourceRegistry;
  // Réalisateur auto (cf. useAutoDirector) — transmis tel quel à la ControlBar.
  autoDirectorEnabled     ?: boolean;
  onAutoDirectorToggle    ?: (on: boolean) => void;
  autoDirectorStatus      ?: AutoDirectorStatus;
  autoDirectorSettings    ?: AutoDirectorSettings;
  onAutoDirectorSettingsChange ?: (next: AutoDirectorSettings) => void;
  autoDirectorCandidates  ?: AutoDirectorCandidate[];

  // ── Synchronisation HlsAudioPanel ────────────────────────────────────────
  hlsVolume?         : number;
  hlsMuted?          : boolean;
  onHlsVolumeChange? : (v: number) => void;
  onHlsMutedChange?  : (m: boolean) => void;
  monitorHlsUrl?     : string;
  broadcastingChannel: BroadcastingChannel;
  // Multi-angle : valeur courante (BroadcastingChannel.multiview) et remontée de la
  // bascule faite par la ControlBar, que `broadcastingChannel` ne reflète pas.
  multiview          ?: boolean;
  onMultiviewChange  ?: (value: boolean) => void;
  onRemoveSource       : (sourceId: string) => void;
  onReloadSource       : (sourceId: string) => Promise<void>;
  // Réacquisition de l'entrée audio d'un slot, déclenchée depuis la fenêtre de
  // réglage du deck — l'équivalent audio de `onReloadSource`.
  onReloadAudio        : (sourceId: string) => Promise<void>;
  onRenameSource       : (sourceId: string, label: string) => void;
  hlsAudioRegistry?  : AudioSourceRegistry;
  hlsAudioSourceId?  : string;
  onHlsReset?: () => void;
  localMics?: { deviceId: string; label: string }[];

  // ── Intercomm ─────────────────────────────────────────────────────────────
  intercommMicStream?       : MediaStream | null;
  intercommDeviceStreams?   : Map<string, MediaStream>;
  intercommSpeakingDevices? : Set<string>;
  onIntercommActivate?      : (active: boolean, selectedDevices: Set<string>) => void;
  onIntercommMicChange?     : (deviceId: string) => void;
}

// ─── Styles partagés ──────────────────────────────────────────────────────────


// ─── Composant principal ──────────────────────────────────────────────────────

export const LiveStreamLayoutV2: React.FC<LiveStreamLayoutV2Props> = (props) => {
  const {
    isHolder = true,
    previewMuted, onTogglePreviewMuted, finalMuted, onToggleFinalMuted,
    canvasPreviewRef, hlsMonitorStream, hasCurrentStream,
    isStreaming, isStreamButtonActive, activeJingleId,
    isStartingStream, isStoppingStream, onStartStreaming, onStopStreaming,
    isRecording, isTogglingRecording, recordingElapsedSec, remainingRecordingSec,
    onStartRecording, onStopRecording, onOpenStorage,
    remoteDevices, remoteConnections, serverUrl, streamPath,
    onLoadDevices, onRegisterDevice, onDeleteDevice, onDisconnectDevice, onRegeneratePassword,
    onSetDirector, directorDeviceIds, deviceNetworkQuality, deviceGeo, onGeoWatch,
    onMetricsLog,
    programLinkActive, programLinkOnAir, onCloseProgramLink, onReopenProgramLink,
    deckPreviewVideoRef, deckPreviewLabel,
    deckSlots, deckSlotLayouts, onSlotLayoutChange, onDeckPreviewLayout, activeLayoutPreviewId,
    selectedVideoSource, onDeckSwitch, onDeckPreview, onAcquirePreview,
    deckPreviewSourceId, onDeckCut, previewStreams,
    tbarValue, tbarValueRef, tbarBaseline, onTbarChange, onTbarAuto, tbarAutoActive, tbarSpeedSec, onTbarSpeedChange,
    tbarEnchainementId, onTbarEnchainementChange,
    tbarEnchainementConfig, onTbarEnchainementConfigChange,
    imageSlots, activeImageIds, onToggleImage,
    soundSlots, activeSoundIds, onTriggerSound,
    jingleSlots, onTriggerJingle,
    videoOverlaySlots, activeVideoOverlayIds, pipVideoEls, onStartVideoOverlay,
    keyMap, onOpenMediaboardConfig,
    showMediaboardConfig, onCloseMediaboardConfig,
    onImageChange, onSoundChange, onPipChange, onJingleChange, loadedAudioBuffers,
    showKeyboardShortcuts, onCloseKeyboardShortcuts, onKeyMapChange,
    videoSlotLabels, imageSlotLabels, soundSlotLabels, pipSlotLabels, jingleSlotLabels,
    screenShareSlots, screenShareStreams, onScreenSlotsChange, onScreenReset,
    channelActive,
    channelFullyActive,
    channelReadyForRecording,
    availableSources, slotConfigs, onSlotConfigsChange, onSlotsReorder,
    closeOnFullscreen, onToggleCloseOnFullscreen, onOpenFullscreen,
    sourceAliases, onSourceAliasesChange,
    showSourceAliasConfig, onOpenSourceAliasConfig, onCloseSourceAliasConfig,
    onSourceConfigOpen, onSourceConfigClose,
    hlsProbeStatusMap, hlsWasEverOk, onRetryHls, onSlotPowerToggle, poweredOffSlots,
    streamSource: streamSourceProp,
    onStreamSourceChange, onStopStreamingExternal,
    isRegiePowered, onOpenDeckFullscreen,
    layoutSlots, activeLayoutId, onToggleLayout, onLayoutChange,
    getSourceAudioConfig, onSourceVolumeChange, onSourceMuteToggle,
    isDeckExternal, isMultiviewExternal,
    availableAudioDevices, onSlotSourceChange, onDeckSwitchSource, onAliasChange,
    selectedAudioDeviceIds, onAudioDeviceChange,
    stereoVolumes, onStereoVolumeChange, audioEffectsMap, onToggleAudioEffect,
    audioEffectConfigsMap, onAudioEffectConfigsChange,
    videoEffectsSettingsMap, onVideoEffectsSettingsChange, onAudioVisualChange,
    onSlotSourceChangeAlias,
    onOpenDevicesPanel,
    onSaveConfig, onLoadConfig, onResetRegie,
    onDefaultStreamSourceChange,
    broadcastUuid,
    onRegisterXtreamConnection,
    microserviceUrl,
    audioSwitchState,
    onAudioSwitchChange,
    deckAttachedAudioIds,
    mixSourceIds,
    onMixerDraftChange,
    audioSourceRegistry,
    autoDirectorEnabled,
    onAutoDirectorToggle,
    autoDirectorStatus,
    autoDirectorSettings,
    onAutoDirectorSettingsChange,
    autoDirectorCandidates,
    hlsVolume,
    hlsMuted,
    onHlsVolumeChange,
    onHlsMutedChange,
    monitorHlsUrl,
    broadcastingChannel,
    multiview,
    onMultiviewChange,
    onRemoveSource,
    onReloadSource, onReloadAudio,
    onRenameSource,
    hlsAudioRegistry,
    hlsAudioSourceId,
    onHlsReset,
    localMics,
    // ── Intercomm ─────────────────────────────────────────────────────────
    intercommMicStream,
    intercommDeviceStreams,
    intercommSpeakingDevices,
    onIntercommActivate,
    onIntercommMicChange,
  } = props;

  const isStreamBusy = isStartingStream || isStoppingStream;

  const [mediaboardConfigInitial, setMediaboardConfigInitial] = useState<{ tab?: ConfigTab; slotIdx?: number }>({});
  const handleOpenMediaboardConfig = useCallback((tab?: ConfigTab, slotIdx?: number) => {
    setMediaboardConfigInitial({ tab, slotIdx });
    onOpenMediaboardConfig(tab, slotIdx);
  }, [onOpenMediaboardConfig]);

  const streamSource        = streamSourceProp        ?? 'video';
  const defaultStreamSource = props.defaultStreamSource ?? 'video';

  const deckPupitreOpenRef = useRef<() => void>(() => {});

  // Stabilisés : passés en lambdas inline, ils changeaient d'identité à chaque
  // rendu et bloquaient toute mémoïsation en aval.
  const handleOpenPupitreConfig   = useCallback(() => deckPupitreOpenRef.current?.(), []);
  const handleRegisterPupitreOpen = useCallback((fn: () => void) => { deckPupitreOpenRef.current = fn; }, []);

  const handleStopStreaming = () => {
    onStopStreaming();
    onStopStreamingExternal?.();
  };

  const handleStreamSourceChange = useCallback((newSource: StreamSource) => {
    if (newSource === 'webrtc' && !isStreaming && !isStreamBusy) {
      onStreamSourceChange?.(newSource);
      onStartStreaming();
      return;
    }
    // « Quitter le mode régie » n'a de sens que si la régie EST à l'antenne. La publication vers
    // le serveur reste ouverte hors antenne (isStreaming vrai sur RTMP ou playlist) : sans ce
    // garde, un clic sur Playlist depuis RTMP lançait l'arrêt de « Live régie » (halo gris) et
    // basculait sur la source par défaut au lieu de celle choisie.
    if (newSource !== 'webrtc' && streamSource === 'webrtc' && isStreaming && !isStreamBusy && hasCurrentStream) {
      handleStopStreaming();
      return;
    }
    onStreamSourceChange?.(newSource);
  }, [onStreamSourceChange, streamSource, isStreaming, isStreamBusy, hasCurrentStream, onStartStreaming, handleStopStreaming]);

  const handleDefaultStreamSourceChange = useCallback((newSource: StreamSource) => {
    onDefaultStreamSourceChange?.(newSource);
  }, [onDefaultStreamSourceChange]);

  // Multiview dans un autre onglet : le panneau reste monté ici (mêmes flux, même état),
  // seul son DOM est rendu dans l'onglet ouvert.
  const detachedMultiview = useDetachedWindow('Kleon — Multiview');
  const handleOpenMultiviewTab = useCallback((closePanel: boolean) => {
    if (!detachedMultiview.open()) {
      window.alert("Le navigateur a bloqué l'ouverture du nouvel onglet. Autorisez les popups pour ce site puis réessayez.");
      return;
    }
    onOpenFullscreen(closePanel);
  }, [detachedMultiview, onOpenFullscreen]);

  const multiviewPanel = (
  <MultiviewPanelV2
    poweredOffSlots={poweredOffSlots}
    microserviceUrl={microserviceUrl}
    canvasPreviewRef={canvasPreviewRef}
    isStreaming={isStreaming}
    deckPreviewVideoRef={deckPreviewVideoRef}
    deckPreviewLabel={deckPreviewLabel}
    deckPreviewSourceId={deckPreviewSourceId}
    activeJingleId={activeJingleId}
    previewMuted={previewMuted}
    onTogglePreviewMuted={onTogglePreviewMuted}
    finalMuted={finalMuted}
    onToggleFinalMuted={onToggleFinalMuted}
    deckSlots={deckSlots}
    previewStreams={previewStreams}
    onDeckPreview={onDeckPreview}
    onDeckSwitch={onDeckSwitch}
    onAcquirePreview={onAcquirePreview}
    selectedVideoSource={selectedVideoSource}
    channelActive={channelActive}
    availableSources={availableSources}
    layoutSlots={layoutSlots}
    slotConfigs={slotConfigs}
    onSlotConfigsChange={onSlotConfigsChange}
    onSlotsReorder={onSlotsReorder}
    closeOnFullscreen={closeOnFullscreen}
    onToggleCloseOnFullscreen={onToggleCloseOnFullscreen}
    onOpenFullscreen={handleOpenMultiviewTab}
    sourceAliases={sourceAliases}
    streamSource={streamSource}
    defaultStreamSource={defaultStreamSource}
    handleStreamSourceChange={handleStreamSourceChange}
    handleDefaultStreamSourceChange={handleDefaultStreamSourceChange}
    hlsMonitorStream={hlsMonitorStream}
    channelUuid={broadcastUuid}
    hlsVolume={hlsVolume}
    hlsMuted={hlsMuted}
    onHlsVolumeChange={onHlsVolumeChange}
    onHlsMutedChange={onHlsMutedChange}
    monitorHlsUrl={monitorHlsUrl}
    broadcastingChannel={broadcastingChannel}
    hlsAudioRegistry={hlsAudioRegistry}
    hlsAudioSourceId={hlsAudioSourceId}
    onHlsReset={onHlsReset}
    remoteDevices={remoteDevices}
    localMics={localMics}
    deviceGeo={deviceGeo}
    onGeoWatch={onGeoWatch}
    onMetricsLog={onMetricsLog}
    programLinkActive={programLinkActive} programLinkOnAir={programLinkOnAir} onCloseProgramLink={onCloseProgramLink}
    onReopenProgramLink={onReopenProgramLink}
    // ── Intercomm ──────────────────────────────────────────────
    intercommMicStream={intercommMicStream}
    intercommDeviceStreams={intercommDeviceStreams}
    intercommSpeakingDevices={intercommSpeakingDevices}
    onIntercommActivate={onIntercommActivate}
    onIntercommMicChange={onIntercommMicChange}
  />
  );

  return (
    <>

      {/* ════════ LIGNE 1 : Barre de contrôle ════════ */}
      <div style={{ marginTop: 8 }}>
        <ControlBar
          hasCurrentStream={hasCurrentStream}
          isStreaming={isStreaming}
          isStreamButtonActive={isStreamButtonActive}
          isStartingStream={isStartingStream}
          isStoppingStream={isStoppingStream}
          onStartStreaming={onStartStreaming}
          handleStopStreaming={handleStopStreaming}
          isRecording={isRecording}
          isTogglingRecording={isTogglingRecording}
          recordingElapsedSec={recordingElapsedSec}
          remainingRecordingSec={remainingRecordingSec}
          onStartRecording={onStartRecording}
          onStopRecording={onStopRecording}
          onOpenStorage={onOpenStorage}
          isRegiePowered={isRegiePowered}
          onOpenPupitreConfig={handleOpenPupitreConfig}
          onOpenMediaboardConfig={handleOpenMediaboardConfig}
          onOpenDeckFullscreen={onOpenDeckFullscreen}
          remoteDevices={remoteDevices}
          remoteConnections={remoteConnections}
          onRegisterDevice={onRegisterDevice}
          onDeleteDevice={onDeleteDevice}
          onDisconnectDevice={onDisconnectDevice}
          onRegeneratePassword={onRegeneratePassword}
          onSetDirector={onSetDirector}
          directorDeviceIds={directorDeviceIds}
        deviceNetworkQuality={deviceNetworkQuality}
          onLoadDevices={onLoadDevices}
          serverUrl={serverUrl}
          streamPath={streamPath}
          webRtcPageUrl={WWW_URLS.ProductControlRoom}
          channelName={broadcastingChannel?.name ?? ''}
          programOrientation={broadcastingChannel?.orientation === 'portrait' ? 'portrait' : 'landscape'}
          onOpenDevicesPanel={onOpenDevicesPanel ?? (() => {})}
          onSaveConfig={onSaveConfig}
          onLoadConfig={onLoadConfig}
          onRegisterXtreamConnection={onRegisterXtreamConnection ?? (() => {})}
          sourceAliases={sourceAliases}
          onAliasesChange={onSourceAliasesChange}
          microserviceUrl={microserviceUrl ?? ''}
          audioSwitchState={audioSwitchState}
          onAudioSwitchChange={onAudioSwitchChange}
          deckAttachedAudioIds={deckAttachedAudioIds}
          mixSourceIds={mixSourceIds}
          onMixerDraftChange={onMixerDraftChange}
          mixMeterRegistry={audioSourceRegistry}
          autoDirectorEnabled={autoDirectorEnabled}
          onAutoDirectorToggle={onAutoDirectorToggle}
          autoDirectorStatus={autoDirectorStatus}
          autoDirectorSettings={autoDirectorSettings}
          onAutoDirectorSettingsChange={onAutoDirectorSettingsChange}
          autoDirectorCandidates={autoDirectorCandidates}
          availableAudioDevices={availableAudioDevices}
          availableSources={availableSources}
          hlsProbeStatusMap={hlsProbeStatusMap}
          selectedVideoSource={selectedVideoSource}
          deckPreviewSourceId={deckPreviewSourceId}
          onRemoveSource={onRemoveSource}
          onReloadSource={onReloadSource}
          onRenameSource={onRenameSource}
		  channelActive={channelActive}
		  channelFullyActive={channelFullyActive}
		  channelReadyForRecording={channelReadyForRecording}
		  broadcastingChannelId={broadcastingChannel?.id ?? 0}
		  multiview={multiview ?? broadcastingChannel?.multiview ?? false}
		  publicPortal={broadcastingChannel?.publicPortal ?? false}
		  onMultiviewChange={onMultiviewChange}
        />
      </div>

      {/* ════════ LIGNE 2 : MultiviewPanelV2 ════════ */}
      <div style={{ display: 'flex', flexDirection: 'column', marginTop: 8 }}>
        {isMultiviewExternal ? (
          <div style={{
            background    : 'rgba(15,23,42,0.5)',
            borderRadius  : 10,
            border        : '1px solid rgba(255,255,255,0.08)',
            padding       : '40px 20px',
            textAlign     : 'center',
            flex          : 1,
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
          }}>
            <span style={{ fontSize: 13, color: 'rgba(148,163,184,0.7)', fontFamily: 'monospace' }}>
              📺 Multiview ouvert dans un onglet externe
            </span>
          </div>
        ) : detachedMultiview.container ? (
          // Multiview parti dans un autre onglet : la place libérée reçoit l'Intercomm (à gauche)
          // et la diffusion du flux final (à droite), montés ICI et nulle part ailleurs.
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'monospace', fontSize: 11, color: 'rgba(148,163,184,0.7)' }}>
              <span>📺 Multiview ouvert dans un autre onglet</span>
              <button type="button" onClick={detachedMultiview.focus}
                style={{ fontSize: 11, padding: '2px 8px', cursor: 'pointer' }}>Afficher l'onglet</button>
              <button type="button" onClick={() => detachedMultiview.close()}
                style={{ fontSize: 11, padding: '2px 8px', cursor: 'pointer' }}>Rapatrier ici</button>
            </div>
            <div>
              {React.cloneElement(multiviewPanel, { sideOnly: true })}
            </div>
          </div>
        ) : (
          multiviewPanel
        )}
        {detachedMultiview.container && ReactDOM.createPortal(
          <PortalTargetContext.Provider value={detachedMultiview.container}>
            <div style={{ height: '100vh', padding: 8, boxSizing: 'border-box', position: 'relative' }}>
              {multiviewPanel}
              {/* Logo en surimpression, hors flux : il ne décale aucune des tailles calculées.
                  Chat ouvert : le logo passe dans la colonne de droite, avec le fil du chat. */}
              {!(broadcastingChannel.chatModeration >= 0) && (
                // Largeur disponible = marge libre à gauche des blocs centrés : (fenêtre − contenu) / 2, moins
                // le retrait du logo et un jour. Contenu = 2 cartes (cf. MultiviewPanelV2) + gouttière 4 +
                // marges 12 + panneau 2 + marge de la page 16.
                <div style={{ position: 'absolute', top: 14, left: 14, zIndex: 5,
                              width: 'max(0px, calc((100vw - (2 * ((50vh - 61px) * 16 / 9 + 5px) + 34px)) / 2 - 14px - 8px))' }}>
                  <KleonLogo responsive />
                </div>
              )}
            </div>
          </PortalTargetContext.Provider>,
          detachedMultiview.container,
        )}
      </div>

      {/* ════════ LIGNE 3 : DeckV2 ════════ */}
      {isDeckExternal ? (
        <div style={{
          marginTop   : 8,
          background  : 'rgba(15,23,42,0.5)',
          borderRadius: 10,
          border      : '1px solid rgba(255,255,255,0.08)',
          padding     : '40px 20px',
          textAlign   : 'center',
        }}>
          <span style={{ fontSize: 13, color: 'rgba(148,163,184,0.7)', fontFamily: 'monospace' }}>
            🎛 Deck ouvert dans un onglet externe
          </span>
        </div>
      ) : (
        /* onSlotsReorder : le MÊME point d'entrée que les mini-vues du multiview.
           Deck, rangée « Preview source » et mini-vues réordonnent un unique
           tableau d'emplacements, chez le parent — c'est ce qui évite la boucle. */
        <DeckV2
          audioSourceRegistry={audioSourceRegistry}
          isHolder={isHolder}
          onReloadSource={onReloadSource}
          onReloadAudio={onReloadAudio}
          deckSlots={deckSlots}
          deckSlotLayouts={deckSlotLayouts}
          onSlotLayoutChange={onSlotLayoutChange}
          onDeckPreviewLayout={onDeckPreviewLayout}
          activeLayoutPreviewId={activeLayoutPreviewId}
          selectedVideoSource={selectedVideoSource}
          onSlotsReorder={onSlotsReorder}
          onDeckSwitch={onDeckSwitch}
          onDeckPreview={onDeckPreview}
          deckPreviewSourceId={deckPreviewSourceId}
          onDeckCut={onDeckCut}
          previewStreams={previewStreams}
          channelPublicRef={streamPath}
          serverUrl={serverUrl}
          channelActive={channelActive}
          imageSlots={imageSlots}
          activeImageIds={activeImageIds}
          onToggleImage={onToggleImage}
          onImageChange={onImageChange}
          soundSlots={soundSlots}
          activeSoundIds={activeSoundIds}
          onTriggerSound={onTriggerSound}
          onSoundChange={onSoundChange}
          jingleSlots={jingleSlots}
          activeJingleId={activeJingleId}
          onTriggerJingle={onTriggerJingle}
          onJingleChange={onJingleChange}
          videoOverlaySlots={videoOverlaySlots}
          activeVideoOverlayIds={activeVideoOverlayIds}
          pipVideoEls={pipVideoEls}
          onStartVideoOverlay={onStartVideoOverlay}
          keyMap={keyMap}
          onKeyMapChange={onKeyMapChange}
          onResetRegie={onResetRegie}
          onOpenMediaboardConfig={handleOpenMediaboardConfig}
          onOpenSourceAliasConfig={onOpenSourceAliasConfig}
          onOpenDeckFullscreen={onOpenDeckFullscreen}
          sourceAliases={sourceAliases}
          layoutSlots={layoutSlots}
          activeLayoutId={activeLayoutId}
          onToggleLayout={onToggleLayout}
          onLayoutChange={onLayoutChange}
          getSourceAudioConfig={getSourceAudioConfig}
          onSourceVolumeChange={onSourceVolumeChange}
          onSourceMuteToggle={onSourceMuteToggle}
          availableSources={availableSources}
          availableAudioDevices={availableAudioDevices}
          onSlotSourceChange={onSlotSourceChange}
          onDeckSwitchSource={onDeckSwitchSource}
          onAliasChange={onAliasChange}
          onAliasesChange={onSourceAliasesChange}
          selectedAudioDeviceIds={selectedAudioDeviceIds}
          onAudioDeviceChange={onAudioDeviceChange}
          stereoVolumes={stereoVolumes}
          onStereoVolumeChange={onStereoVolumeChange}
          audioEffectsMap={audioEffectsMap}
          onToggleAudioEffect={onToggleAudioEffect}
          audioEffectConfigsMap={audioEffectConfigsMap}
          onAudioEffectConfigsChange={onAudioEffectConfigsChange}
          videoEffectsSettingsMap={videoEffectsSettingsMap}
          onVideoEffectsSettingsChange={onVideoEffectsSettingsChange}
          onAudioVisualChange={onAudioVisualChange}
          tbarValue={tbarValue}
          tbarValueRef={tbarValueRef}
          tbarBaseline={tbarBaseline}
          onTbarChange={onTbarChange}
          onTbarAuto={onTbarAuto}
          tbarAutoActive={tbarAutoActive}
          tbarSpeed={tbarSpeedSec}
          onTbarSpeedChange={onTbarSpeedChange}
          tbarEnchainementId={tbarEnchainementId}
          onTbarEnchainementChange={onTbarEnchainementChange}
          tbarEnchainementConfig={tbarEnchainementConfig}
          onTbarEnchainementConfigChange={onTbarEnchainementConfigChange}
          onSourceConfigOpen={onSourceConfigOpen}
          onSourceConfigClose={onSourceConfigClose}
          hlsProbeStatusMap={hlsProbeStatusMap}
          hlsWasEverOk={hlsWasEverOk}
          onRetryHls={onRetryHls}
          onSlotPowerToggle={onSlotPowerToggle}
          poweredOffSlots={poweredOffSlots}
          isRegiePowered={isRegiePowered}
          onRegisterPupitreOpen={handleRegisterPupitreOpen}
        />
      )}

      <style>{`
        @keyframes recPing {
          0%, 100% { transform: scale(1); opacity: 0.75; }
          50%       { transform: scale(1.8); opacity: 0; }
        }
        @keyframes liveGlowGreen {
          0%, 100% { box-shadow: 0 0 6px 2px rgba(34,197,94,0.45); }
          50%       { box-shadow: 0 0 18px 7px rgba(34,197,94,0.85); }
        }
        @keyframes liveGlowRed {
          0%, 100% { box-shadow: 0 0 6px 2px rgba(220,38,38,0.45); }
          50%       { box-shadow: 0 0 18px 7px rgba(220,38,38,0.85); }
        }
        @keyframes liveGlowGrey {
          0%, 100% { box-shadow: 0 0 6px 2px rgba(148,163,184,0.35); }
          50%       { box-shadow: 0 0 18px 7px rgba(148,163,184,0.7); }
        }
      `}</style>

      {/* ════════ MODALES ════════ */}

      {showMediaboardConfig && (
        <MediaboardConfigModal
          initialTab={mediaboardConfigInitial.tab}
          initialLayoutIdx={mediaboardConfigInitial.tab === 'layouts' ? mediaboardConfigInitial.slotIdx : undefined}
          initialMediaIdx={mediaboardConfigInitial.tab !== 'layouts' ? mediaboardConfigInitial.slotIdx : undefined}
          imageSlots={imageSlots}
          soundSlots={soundSlots}
          videoOverlaySlots={videoOverlaySlots}
          jingleSlots={jingleSlots}
          remoteDevices={remoteDevices}
          onImageChange={onImageChange}
          onSoundChange={onSoundChange}
          onPipChange={onPipChange}
          onJingleChange={onJingleChange}
          loadedAudioBuffers={loadedAudioBuffers}
          onClose={onCloseMediaboardConfig}
          screenShareSlots={screenShareSlots}
          screenShareStreams={screenShareStreams}
          onScreenSlotsChange={onScreenSlotsChange}
          onScreenReset={onScreenReset}
          layoutSlots={layoutSlots}
          onLayoutChange={onLayoutChange}
          availableSources={availableSources}
          channelName={broadcastingChannel?.name ?? 'Chaîne'}
          channelUuid={broadcastUuid ?? broadcastingChannel?.uuid ?? ''}
        />
      )}

      {showKeyboardShortcuts && (
        <KeyboardShortcutsModal
          keyMap={keyMap}
          onClose={onCloseKeyboardShortcuts}
          onChange={onKeyMapChange}
          videoSlotLabels={videoSlotLabels}
          imageSlotLabels={imageSlotLabels}
          soundSlotLabels={soundSlotLabels}
          pipSlotLabels={pipSlotLabels}
          jingleSlotLabels={jingleSlotLabels}
          layoutSlotLabels={layoutSlots.map((s, i) =>
            s.background.type !== 'none' || s.incrustations.length > 0
              ? (s.label || `Layout ${i + 1}`)
              : ''
          )}
        />
      )}

      {showSourceAliasConfig && (
        <SourceAliasConfigModal
          availableSources={availableSources}
          deckSlots={deckSlots}
          deckSlotLayouts={deckSlotLayouts}
          aliases={sourceAliases}
          onAliasesChange={onSourceAliasesChange}
          onSlotSourceChange={onSlotSourceChangeAlias}
          onSlotLayoutChange={onSlotLayoutChange}
          onClose={onCloseSourceAliasConfig}
          jingleSlots={jingleSlots}
          layoutSlots={layoutSlots}
          activeLayoutId={activeLayoutId}
        />
      )}
    </>
  );
};

// ─── Export du bouton Power ───────────────────────────────────────────────────

export const RegiePowerButton: React.FC<{ isRegiePowered: boolean; onToggle: () => void }> = ({
  isRegiePowered, onToggle,
}) => (
  <button
    onClick={onToggle}
    title={isRegiePowered ? 'Éteindre la régie' : 'Allumer la régie'}
    style={{
      width         : 30,
      height        : 30,
      borderRadius  : 7,
      display       : 'flex',
      alignItems    : 'center',
      justifyContent: 'center',
      border        : `1px solid ${isRegiePowered ? 'rgba(52,211,153,0.5)' : 'rgba(100,116,139,0.4)'}`,
      background    : isRegiePowered ? 'rgba(16,185,129,0.15)' : 'rgba(51,65,85,0.5)',
      color         : isRegiePowered ? '#34d399' : '#64748b',
      cursor        : 'pointer',
      transition    : 'all 0.15s',
      flexShrink    : 0,
    }}
  >
    <Power style={{ width: 13, height: 13 }} />
  </button>
);

export default LiveStreamLayoutV2;